import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import ReAddHoldingDialog from "@/components/benchmark/ReAddHoldingDialog";

interface CbamFund { id: string; fe_fund_name: string; fund_manager: string | null; fund_name: string; fund_class: string | null; isin: string | null; is_current: boolean; }
interface FundWeight { id: string; fund_id: string; portfolio: number; weight_pct: number; effective_date: string; }

const P = 1;

export default function MmFundManagement() {
  const queryClient = useQueryClient();
  const [showAddFund, setShowAddFund] = useState(false);
  const [newFund, setNewFund] = useState({ fund_name: "", fe_fund_name: "", fund_manager: "", isin: "" });

  const { data: funds } = useQuery({
    queryKey: ["ba-cbam-funds-all", "v2-with-class-isin"],
    queryFn: async () => {
      const { data, error } = await supabase.from("ba_cbam_funds").select("id, fe_fund_name, fund_manager, fund_name, fund_class, isin, is_current").order("fund_name");
      if (error) throw error;
      return data as CbamFund[];
    },
  });

  const { data: weights } = useQuery({
    queryKey: ["ba-mm-fund-weights-all"],
    queryFn: async () => {
      const { data, error } = await supabase.from("ba_mm_cbam_fund_weights").select("*");
      if (error) throw error;
      return data as FundWeight[];
    },
  });

  const addFundMutation = useMutation({
    mutationFn: async (fund: typeof newFund) => {
      if (!fund.isin.trim()) throw new Error("ISIN is required");
      const { error } = await supabase.from("ba_cbam_funds").insert({
        fund_name: fund.fund_name, fe_fund_name: fund.fe_fund_name, fund_manager: fund.fund_manager || null, isin: fund.isin.trim(),
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ba-cbam-funds-all"] });
      setShowAddFund(false);
      setNewFund({ fund_name: "", fe_fund_name: "", fund_manager: "", isin: "" });
      toast.success("Fund added");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const updateWeightMutation = useMutation({
    mutationFn: async ({ fund_id, weight_pct }: { fund_id: string; weight_pct: number }) => {
      const { error } = await supabase.from("ba_mm_cbam_fund_weights").upsert(
        { fund_id, portfolio: P, weight_pct, effective_date: new Date().toISOString().split("T")[0] },
        { onConflict: "fund_id,portfolio,effective_date" }
      );
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["ba-mm-fund-weights-all"] }),
  });

  const removeFundMutation = useMutation({
    mutationFn: async (fundId: string) => {
      // Exit the fund by dating it out, never by deleting. ba_mm_cbam_fund_weights is
      // effective-dated and holds the deepest history in the estate (back to 2019); every
      // historical reconstruction reads "the latest row per fund on or before date X", so a
      // DELETE erases all of it. Writing weight_pct = 0 at today's date is the schema's exit
      // convention: the latest weight becomes 0, so the fund drops out of this table, which
      // filters on latest weight > 0.
      const today = new Date().toISOString().split("T")[0];
      const { error } = await supabase.from("ba_mm_cbam_fund_weights").upsert(
        { fund_id: fundId, portfolio: P, weight_pct: 0, effective_date: today },
        { onConflict: "fund_id,portfolio,effective_date" }
      );
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ba-mm-fund-weights-all"] });
      toast.success("Fund removed from MM");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const getWeight = (fundId: string) => {
    const rows = (weights || []).filter((w) => w.fund_id === fundId && w.portfolio === P);
    if (rows.length === 0) return 0;
    const latest = rows.reduce((a, b) => (a.effective_date > b.effective_date ? a : b));
    return latest.weight_pct;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">MM Fund Holdings and Weightings</h1>
          <p className="text-sm text-muted-foreground">Manage CBAM fund holdings for the Money Markets portfolio</p>
        </div>
        <div className="flex items-center gap-2">
        <ReAddHoldingDialog
          weightsTable="ba_mm_cbam_fund_weights"
          portfolios={[{ num: P, label: "MM" }]}
          rangeLabel="MM"
          invalidateKeys={[["ba-cbam-funds-all"], ["ba-mm-fund-weights-all"]]}
        />
        <Dialog open={showAddFund} onOpenChange={setShowAddFund}>
          <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-1" /> Add Fund</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add New Fund</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Display Name</Label><Input value={newFund.fund_name} onChange={(e) => setNewFund({ ...newFund, fund_name: e.target.value })} /></div>
              <div><Label>FE Fund Name (exact match)</Label><Input value={newFund.fe_fund_name} onChange={(e) => setNewFund({ ...newFund, fe_fund_name: e.target.value })} /></div>
              <div><Label>Fund Manager</Label><Input value={newFund.fund_manager} onChange={(e) => setNewFund({ ...newFund, fund_manager: e.target.value })} /></div>
              <div><Label>ISIN <span className="text-destructive">*</span></Label><Input value={newFund.isin} onChange={(e) => setNewFund({ ...newFund, isin: e.target.value.toUpperCase() })} maxLength={12} /></div>
              <p className="text-xs text-muted-foreground">
                For a fund that was previously held and sold out, use "Re-add Holding" instead —
                adding it again here fails on the unique FE fund name.
              </p>
              <Button onClick={() => addFundMutation.mutate(newFund)} disabled={!newFund.fund_name || !newFund.fe_fund_name || !newFund.isin.trim()}>Add Fund</Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-lg">Fund Holdings & Weights</CardTitle></CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="text-left p-3 font-medium text-muted-foreground min-w-[100px]">Manager</th>
                <th className="text-left p-3 font-medium text-muted-foreground min-w-[200px]">Fund</th>
                <th className="text-left p-3 font-medium text-muted-foreground min-w-[100px]">Fund Class</th>
                <th className="text-left p-3 font-medium text-muted-foreground min-w-[120px]">ISIN</th>
                <th className="text-center p-3 font-medium text-muted-foreground min-w-[80px]">MM %</th>
                <th className="text-center p-3 font-medium text-muted-foreground min-w-[60px]">Active</th>
                <th className="text-center p-3 font-medium text-muted-foreground min-w-[60px]">Remove</th>
              </tr>
            </thead>
            <tbody>
              {funds?.filter((fund) => getWeight(fund.id) > 0).slice().sort((a, b) => {
                const m = (a.fund_manager || "").localeCompare(b.fund_manager || "", undefined, { sensitivity: "base" });
                if (m !== 0) return m;
                const f = (a.fund_name || "").localeCompare(b.fund_name || "", undefined, { sensitivity: "base" });
                if (f !== 0) return f;
                return (a.fund_class || "").localeCompare(b.fund_class || "", undefined, { sensitivity: "base" });
              }).map((fund, idx) => (
                <tr key={fund.id} className={cn("border-b", idx % 2 === 0 ? "bg-background" : "bg-muted/20")}>
                  <td className="p-3 text-xs text-muted-foreground">{fund.fund_manager || "—"}</td>
                  <td className="p-3 text-xs font-medium">{fund.fund_name}</td>
                  <td className="p-3 text-xs text-muted-foreground">{fund.fund_class || "—"}</td>
                  <td className="p-3 text-xs font-mono text-muted-foreground">{fund.isin || <span className="text-destructive">—</span>}</td>
                  <td className="p-2 text-center">
                    <Input
                      key={`${fund.id}-${getWeight(fund.id)}`}
                      type="number" step="0.01"
                      className="w-20 h-7 text-xs text-center mx-auto"
                      defaultValue={Number(getWeight(fund.id)).toFixed(2)}
                      onBlur={(e) => {
                        const val = parseFloat(e.target.value);
                        if (!isNaN(val)) updateWeightMutation.mutate({ fund_id: fund.id, weight_pct: val });
                      }}
                    />
                  </td>
                  <td className="p-2 text-center">
                    {/* Held-status comes from the MM weight, NOT from ba_cbam_funds.is_current.
                        is_current is derived by ba_fund_has_active_weight(), which reads only the
                        P, R and Dist weight tables — ba_mm_cbam_fund_weights is not consulted and
                        has no trg_sync_is_current trigger. Every row in this table is filtered to
                        latest MM weight > 0, so it is held in MM by construction; showing
                        is_current here labelled 6 of the 8 live MM holdings "Legacy". */}
                    <Badge variant="default" className="text-[10px]">Held</Badge>
                  </td>
                  <td className="p-2 text-center">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remove {fund.fund_name} from MM?</AlertDialogTitle>
                          <AlertDialogDescription>This sets the MM weight to 0% as of today, so the fund drops out of the MM range. Its weight history is kept.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => removeFundMutation.mutate(fund.id)}>Remove</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
